/**
 * Trip wizard state, effects, and handlers for CreateTripV2.
 * Legacy CreateTrip.js is intentionally left untouched.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from 'auth/Auth';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

import stepsConfig from '../../stepsConfig';
import { fetchGroupedCitiesForCountries, invalidateGroupedCitiesCache } from 'api/countriesNow';
import { previewPrivateObject, uploadPendingTripImage } from '../../../../../utils/S3';
import { normalizeContinentLabel } from '../../../../../data/continentCountries';
import {
    WIZARD_STEPS,
    WIZARD_STEP_CONFIG_KEYS,
    getStepConfigKey,
    EMPTY_TRIP_FORM,
} from '../constants';
import {
    sanitizePayload,
    deepTrimStrings,
    applyDefaults,
    withSavedDates,
    DEPARTURE_DATE_KEYS,
    ALLOCATION_DATE_KEYS,
} from '../utils/formHelpers';
import { getApiErrorMessage } from '../errors/getApiErrorMessage';
import { formatValidationMessage } from '../errors/formatValidationMessage';
import { validateWizardStep, validateAllWizardSteps, computeInitialCompletedSteps, findCrossFieldErrors } from '../validation/validateWizardStep';
import { applyTripStatusToFormData } from '../utils/tripStatus';
import {
    COUNTRY_ALREADY_EXISTS_MESSAGE,
    collectTakenCountries,
    isCountryTaken,
} from '../utils/destinationCountry';
import { applyTripCurrencyToFormData, getTripCurrency } from '../utils/tripCurrency';
import {
    applyReturnDatesToDepartures,
    syncDeparturesReturnDates,
    findDepartureDurationErrorsForForm,
    sortDeparturesByDate,
} from '../utils/tripDates';
import { STEP_ENDPOINTS, STEP_KEYS } from '../stepApi';
import { APPLY_RESPONSE } from '../stepPayloads';
import { buildStepPayload, fingerprint } from '../../utils/stepSnapshot';
import { useScrollContainer } from "shell-v2/ScrollContainer";



// Steps whose fields use `city-autocomplete` (see stepsConfig: packages.city, departures.departure_city)
const CITY_STEP_KEYS = ['packages', 'departures'];

export default function useCreateTripWizard() {
    // The window in classic; <main> in the v2 frame (see shell-v2/ScrollContainer).
    const scroller = useScrollContainer();

    const tripIdRef = useRef(null);            // so back-to-back saves see the id immediately
    const formRef = useRef(null);              // latest form, readable inside async loops
    const savedSnapshotRef = useRef({});       // stepKey -> fingerprint of what the server has
    const EMPTY_FP = useRef({});               // baseline for never-saved steps
    const setTripId = (id) => { tripIdRef.current = id; setTripIdState(id); };

    const [formData, setFormData] = useState(() => ({ ...EMPTY_TRIP_FORM }));
    formRef.current = formData;

    const { axiosInstance } = useAuth();
    const navigate = useNavigate();
    const { editTripId, editPackageId } = useParams();
    const [searchParams] = useSearchParams();

    const [apiError, setApiError] = useState(null);
    const [isSaving, setIsSaving] = useState(false);
    const [tripId, setTripIdState] = useState(null);
    const [tripHeroImage, setTripHeroImage] = useState('');
    const [pendingImageFile, setPendingImageFile] = useState(null);
    const [pendingImagePreviewUrl, setPendingImagePreviewUrl] = useState('');
    const [packageHeroImage, setPackageHeroImage] = useState('');
    const [pendingPackageImageFile, setPendingPackageImageFile] = useState(null);
    const [pendingPackageImagePreviewUrl, setPendingPackageImagePreviewUrl] = useState('');
    const [errors, setErrors] = useState([]);
    const [validationSnackbar, setValidationSnackbar] = useState({ open: false, message: '' });
    const [draftSnackbar, setDraftSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
    const [publishDialogOpen, setPublishDialogOpen] = useState(false);
    const [activeStep, setActiveStep] = useState(0);
    const [completedSteps, setCompletedSteps] = useState(() => new Set());
    const [isTripLoading, setIsTripLoading] = useState(Boolean(editTripId));
    const [isStepDataLoading, setIsStepDataLoading] = useState(false);
    const [isContinuing, setIsContinuing] = useState(false);
    const [takenCountries, setTakenCountries] = useState([]);

    const wizard_steps = WIZARD_STEPS;

    const [tripStatus, setTripStatusState] = useState('draft');

    const hasPendingFiles = (key, fd) => {
        if (key === 'overview') {
            return Boolean(pendingImageFile) || (fd.overview?.key_highlights || []).some((h) => h?.pendingFile);
        }
        if (key === 'packages') {
            return Boolean(pendingPackageImageFile) || (fd.packages?.image_gallery || []).some((h) => h?.pendingFile);
        }
        return false;
    };

    const isStepDirty = (key, fd = formRef.current) => {
        if (key === 'overview' && !tripIdRef.current) return true;
        if (key === 'packages' && !fd.packages?.package_id) return true;
        if (hasPendingFiles(key, fd)) return true;
        // never saved and still empty (optional steps) => clean
        EMPTY_FP.current[key] ??= fingerprint(key, EMPTY_TRIP_FORM);
        const saved = savedSnapshotRef.current[key] ?? EMPTY_FP.current[key];
        return saved !== fingerprint(key, fd);
    };

    const handleTripStatusChange = (nextStatus) => {
        setTripStatusState(nextStatus);
        setFormData((prev) => applyTripStatusToFormData(prev, nextStatus));
    };

    const [tripsList, setTripsList] = useState([]);
    const [isTripsLoading, setIsTripsLoading] = useState(false);

    useEffect(() => {
        let cancelled = false;

        const loadTrips = async () => {
            if (!axiosInstance) return;
            setIsTripsLoading(true);
            try {
                const response = await axiosInstance.get('/trips');
                if (cancelled) return;
                setTripsList(response.data || []);
                setTakenCountries(
                    collectTakenCountries(response.data || [], {
                        excludeTripId: tripId || editTripId || null,
                    })
                );
            } catch (error) {
                console.error('Error loading trips:', error);
                if (!cancelled) {
                    setTripsList([]);
                    setTakenCountries([]);
                }
            } finally {
                if (!cancelled) setIsTripsLoading(false);
            }
        };

        loadTrips();
        return () => {
            cancelled = true;
        };
    }, [axiosInstance, tripId, editTripId]);

    const fetchTrip = async (id, targetPackageId = null) => {
        const tripIdNum = Number(id);
        if (!Number.isInteger(tripIdNum) || tripIdNum <= 0) {
            setApiError(
                'Unable to load trip details: invalid destination id in the URL. Open the destination from Destinations or Merchant Allocation again.'
            );
            setIsTripLoading(false);
            return;
        }
        setIsTripLoading(true);
        try {
            const response = await axiosInstance.get(`/trips/${tripIdNum}`);
            const data = response.data;
            setTripId(data.id);

            const packagesList = data.packages || [];
            // Trip edit needs the primary package; package-scoped edit uses editPackageId.
            const targetPackage = targetPackageId
                ? packagesList.find((p) => String(p.id) === String(targetPackageId))
                : undefined;

            if (targetPackageId && !targetPackage) {
                throw new Error(`Package ${targetPackageId} was not found on this trip.`);
            }

            const {
                base_price, currency, payment_templates, cities, city,
                components, departure_dates, itineraries, addons: packageAddons, faqs,
                ...packageRest
            } = targetPackage || {};

            // Prefer wizard addons endpoint — includes AE/SB extension day schedules.
            let addons = packageAddons || [];
            if (targetPackage?.id) {
                try {
                    const addonsRes = await axiosInstance.get(`/trip-wizard-addons/${targetPackage.id}`);
                    if (Array.isArray(addonsRes.data)) {
                        addons = addonsRes.data.map((a) => ({
                            ...a,
                            // Normalize wizard shape (addon_name) to ORM-like name used below
                            name: a.addon_name ?? a.name,
                            type: a.addon_type ?? a.type,
                        }));
                    }
                } catch (addonsErr) {
                    console.warn('Could not load trip-wizard-addons; falling back to package addons', addonsErr);
                }
            }

            const normalizedPackageCities = Array.isArray(cities) ? cities : [];

            const trip_image = await resolveTripHeroImage(data.id);
            setTripHeroImage(trip_image || '');
            const package_image = targetPackage ? await resolvePackageHeroImage(targetPackage.id) : '';
            setPackageHeroImage(package_image || '');

            const resolvedPackageGallery = targetPackage
                ? await Promise.all(
                    (targetPackage.image_gallery || []).map(async (item, index) => {
                        let image = item.image || '';
                        if (item.image_attachment_id) {
                            try {
                                image = await previewPrivateObject({
                                    axiosInstance,
                                    attachmentId: item.image_attachment_id,
                                }) || image;
                            } catch (previewError) {
                                console.error('Error previewing package gallery attachment:', previewError);
                            }
                        }
                        return {
                            id: item.id || `pgh-${index}`,
                            name: item.name,
                            location: item.location,
                            image,
                            image_attachment_id: item.image_attachment_id,
                        };
                    })
                )
                : [];

            const resolvedHighlights = await Promise.all(
                (data.key_highlights || []).map(async (item, index) => {
                    let image = item.image || '';
                    if (item.image_attachment_id) {
                        try {
                            image = await previewPrivateObject({
                                axiosInstance,
                                attachmentId: item.image_attachment_id,
                            }) || image;
                        } catch (previewError) {
                            console.error('Error previewing highlight attachment:', previewError);
                        }
                    }
                    return {
                        id: item.id || `hl-${index}`,
                        name: item.name,
                        location: item.location,
                        image,
                        image_attachment_id: item.image_attachment_id,
                    };
                })
            );

            const loadedStatus = data.status || targetPackage?.status || 'draft';
            setTripStatusState(loadedStatus);

            // --- Remap components -> ServiceSchema shape ---
            const servicesList = (components || []).map((c) => ({
                id: c.id,
                service_name: c.name,
                category: c.category,
                sub_category: c.sub_category,
                description: c.description,
                supplier: c.merchant_id,
                cost_price: c.cost_price,
                currency: c.currency,
                status: c.status,
                property_name: c.property_name,
                check_in_time: c.check_in_time,
                check_out_time: c.check_out_time,
                property_address: c.property_address,
                min_occupancy: c.min_occupancy,
                max_occupancy: c.max_occupancy,
                pickup_location: c.pickup_location,
                drop_location: c.drop_location,
                pickup_time: c.pickup_time,
                drop_time: c.drop_time,
                max_passenger: c.max_passenger,
                min_passenger: c.min_passenger,
                airline: c.airline,
                flight_number: c.flight_number,
                departure_airport: c.departure_airport,
                arrival_airport: c.arrival_airport,
                departure_time: c.departure_time,
                arrival_time: c.arrival_time,
                baggage_included: c.baggage_included,
                activity_location: c.activity_location,
                start_time: c.start_time,
                end_time: c.end_time,
                duration: c.duration,
                maximum_participants: c.maximum_participants,
                minimum_participants: c.minimum_participants,
                venue: c.venue,
                service_time: c.service_time,
                guide_name: c.guide_name,
                contact_number: c.contact_number,
                languages: c.languages,
                reporting_time: c.reporting_time,
                meeting_point: c.meeting_point,
            }));

            // --- Remap departure_dates -> DepartureSchema shape ---
            const departuresList = sortDeparturesByDate((departure_dates || []).map((d) => withSavedDates({
                id: d.id,
                departure_date: d.departure_date,
                return_date: d.return_date,
                departure_city: d.departure_city,
                status: d.status,
                total_allocation: d.total_allocation,
                minimum_pax: d.minimum_pax,
                maximum_pax: d.maximum_pax,
                book_by_date: d.book_by_date,
                hss_template: d.hss_template_id,
                hss_percentage: d.hss_percentage,
                hss_total: d.hss_amount,
                hss_flat_charge: d.additional_charges,
                discount_percentage: d.discount_percentage,
                offer: d.is_early_bird ? 'early_bird' : (d.is_last_minute_deal ? 'last_minute' : 'none'),
                early_bird_start_date: d.early_bird_start_date,
                early_bird_end_date: d.early_bird_end_date,
                last_minute_start_date: d.last_minute_start_date,
                last_minute_end_date: d.last_minute_end_date,
            }, DEPARTURE_DATE_KEYS)));

            // --- Remap itineraries[0] -> ItinerariesStepSchema shape ---
            const itineraryEntry = itineraries?.[0];
            const itinerariesFormData = itineraryEntry ? {
                id: itineraryEntry.id,
                itinerary_title: itineraryEntry.title,
                inclusion: itineraryEntry.inclusion,
                exclusion: itineraryEntry.exclusion,
                status: itineraryEntry.status,
                days: [...(itineraryEntry.itinerary_days || [])]
                    .sort((a, b) => (a.day_number || 0) - (b.day_number || 0))
                    .map((day) => ({
                        id: day.id,
                        day_number: day.day_number,
                        day_title: day.day_title,
                        line_items: (day.line_items || []).map((li) => ({
                            id: li.id,
                            trip_component_map: li.trip_component_id,
                            item_title: li.title,
                            location: li.location,
                            start_date_time: li.start,
                            end_date_time: li.end,
                            min_pax: li.min_pax,
                            max_pax: li.max_pax,
                            sequence: li.sort_order,
                            mandatory: li.mandatory,
                            day_line_item_description: li.notes,
                        })),
                    })),
            } : { days: [] };

            // --- Remap addons -> AddonSchema shape ---
            const addonsList = (addons || []).map((a) => ({
                id: a.id,
                itinerary_day_id: a.itinerary_day_id,
                addon_name: a.addon_name || a.name,
                addon_type: a.addon_type || a.type,
                addon_price: a.addon_price,
                website_title: a.website_title,
                website_description: a.website_description,
                currency: a.currency,
                status: a.status,
                min_quantity: a.min_quantity,
                max_quantity: a.max_quantity,
                max_extension_days: a.max_extension_days,
                min_extension_days: a.min_extension_days,
                application_scope: a.application_scope,
                linked_component_id: a.linked_component_id,
                pricing_basis: a.pricing_basis || 'per_passenger',
                number_of_days: a.number_of_days
                    || a.extension_days_count
                    || (a.addon_itinerary_days || []).length
                    || a.max_extension_days
                    || a.min_extension_days
                    || '',
                extension_days_count: a.number_of_days
                    || a.extension_days_count
                    || (a.addon_itinerary_days || []).length
                    || a.max_extension_days
                    || a.min_extension_days
                    || '',
                // Omit when missing so PUT does not wipe existing AE/SB days
                // (backend treats null as "leave unchanged", [] as replace).
                ...(Array.isArray(a.addon_itinerary_days)
                    ? {
                        addon_itinerary_days: a.addon_itinerary_days.map((d) => ({
                            id: d.id,
                            day_number: d.day_number,
                            day_title: d.day_title || '',
                            description: d.description || '',
                            guide_instructions: d.guide_instructions || '',
                            application_scope: d.application_scope,
                            extension_position: d.extension_position,
                            line_items: (d.line_items || []).map((li) => ({
                                id: li.id,
                                item_title: li.item_title || '',
                                trip_component_map: li.trip_component_map ?? '',
                                location: li.location || '',
                                sequence: li.sequence ?? 1,
                                day_line_item_description: li.day_line_item_description || '',
                            })),
                        })),
                    }
                    : {}),
            }));

            const loadedFormData = applyTripStatusToFormData(applyTripCurrencyToFormData({
                overview: {
                    description: data.description,
                    continent: normalizeContinentLabel(data.continent),
                    country: data.primary_country,
                    currency: data.currency,
                    languages: data.languages || [],
                    key_highlights: resolvedHighlights,
                    hero_image: trip_image ? 'uploaded' : '',
                },
                packages: targetPackage
                    ? {
                        package_id: packageRest.id,
                        package_title: packageRest.title,
                        package_description: packageRest.description,
                        package_tier: packageRest.package_tier,
                        tags: packageRest.tags,
                        sequence: packageRest.sequence,
                        duration: packageRest.duration_days,
                        status: packageRest.status,
                        city: normalizedPackageCities,
                        trip_ids: (targetPackage.trips || []).map((t) => t.id),
                        image_gallery: resolvedPackageGallery,
                    }
                    : {},
                pricing: { base_price, currency, payment_templates },
                services: { services: servicesList },
                departures: { departures: departuresList },
                allocations: {
                    allocations: (targetPackage?.merchant_allocations || []).map((a) => {
                        const depId = a.departure_date_id || '';
                        const seasonFrom = a.season_from ? String(a.season_from).slice(0, 10) : '';
                        const seasonTo = a.season_to ? String(a.season_to).slice(0, 10) : '';
                        // Bounds from the linked departure window (incl. return), not the
                        // stored season — otherwise edit cannot widen within the trip.
                        const linkedDep = (departure_dates || []).find(
                            (d) => String(d.id) === String(depId),
                        );
                        const boundFrom = linkedDep?.departure_date
                            ? String(linkedDep.departure_date).slice(0, 10)
                            : '';
                        const boundTo = linkedDep?.return_date
                            ? String(linkedDep.return_date).slice(0, 10)
                            : boundFrom;
                        return withSavedDates({
                            id: a.id,
                            name: a.name || '',
                            merchant_id: a.merchant_id || '',
                            status: a.status || 'Pending',
                            rejection_reason: a.rejection_reason || '',
                            rejected_on: a.rejected_on ? String(a.rejected_on).slice(0, 10) : '',
                            allocation_priority: a.allocation_priority ?? 0,
                            pricing_model: a.pricing_model || 'Dated',
                            trip_package_id: a.trip_package_id || packageRest?.id || '',
                            departure_date_id: depId,
                            departure_date_ids: depId !== '' && depId != null ? [depId] : [],
                            package_component_id: a.package_component_id || '',
                            currency: a.currency || currency || 'GBP',
                            priority: a.priority ?? 0,
                            min_number: a.min_number ?? '',
                            season_from: seasonFrom,
                            season_to: seasonTo,
                            season_bound_from: boundFrom,
                            season_bound_to: boundTo,
                            cost: a.cost ?? '',
                        }, ALLOCATION_DATE_KEYS);
                    }),
                },
                itineraries: itinerariesFormData,
                addons: { addons: addonsList },
                faqs: {
                    faqs: (faqs || []).map((f) => ({
                        id: f.id,
                        question: f.question,
                        answer: f.answer,
                        active: f.is_published,
                    })),
                },
            }, currency), loadedStatus);
            const prev = formRef.current || {};
            const resolvedCountry = data.primary_country || prev?.overview?.country || '';
            const resolvedContinent = normalizeContinentLabel(data.continent)
                || prev?.overview?.continent
                || '';
            const prevHighlights = prev?.overview?.key_highlights || [];
            const mergedHighlights = resolvedHighlights.map((apiItem) => {
                const localItem = prevHighlights.find((item) => item.id === apiItem.id);
                if (localItem?.image?.startsWith('blob:')) return localItem;
                if (localItem?.pendingFile && !apiItem.image) return localItem;
                return apiItem;
            });
            prevHighlights.forEach((localItem) => {
                if (!mergedHighlights.some((item) => item.id === localItem.id)) {
                    mergedHighlights.push(localItem);
                }
            });

            const merged = {
                ...loadedFormData,
                overview: {
                    ...loadedFormData.overview,
                    description: data.description || prev?.overview?.description || '',
                    country: resolvedCountry,
                    continent: resolvedContinent || loadedFormData.overview?.continent,
                    key_highlights: mergedHighlights,
                },
            };

            formRef.current = merged;
            setFormData(merged);
            setCompletedSteps(computeInitialCompletedSteps(merged));
            STEP_KEYS.forEach((k) => { savedSnapshotRef.current[k] = fingerprint(k, merged); });
        } catch (error) {
            console.error('Error fetching trip:', error);
            setApiError(getApiErrorMessage(error, { action: 'load trip details', stepKey: 'overview' }));
        } finally {
            setIsTripLoading(false);
        }
    };

    const resolveTripHeroImage = async (resolvedTripId) => {
        try {
            const response = await axiosInstance.get('/attachments/', {
                params: {
                    parent_table: 'travel_orders',
                    parent_id: resolvedTripId,
                },
            });
            return await handlePreviewAttachment(response.data[0]);
        } catch (err) {
            console.error(`Error fetching attachments for trip ${resolvedTripId}:`, err);
            return '';
        }
    };

    const resolvePackageHeroImage = async (resolvedPackageId) => {
        try {
            const response = await axiosInstance.get('/attachments/', {
                params: { parent_table: 'trip_packages', parent_id: resolvedPackageId },
            });
            const heroes = (response.data || [])
                .filter((a) => String(a.storage_path || '').includes('trip-package-banners'))
                .sort((a, b) => (b.id || 0) - (a.id || 0)); // or by created_at desc
            const hero = heroes[0];
            return hero ? await handlePreviewAttachment(hero) : '';
        } catch (err) {
            console.error(`Error fetching attachments for package ${resolvedPackageId}:`, err);
            return '';
        }
    };

    const handlePreviewAttachment = async (attachment) => {
        try {
            if (!attachment.bucket_name || !attachment.storage_path) {
                throw new Error('Attachment storage metadata is invalid for strict R2 download');
            }
            return await previewPrivateObject({
                axiosInstance,
                attachmentId: attachment.id,
            });
        } catch (error) {
            console.error('Error previewing attachment:', error);
        }
    };

    const handlePendingImageSelect = (fieldKey, file, previewUrl) => {
        if (pendingImagePreviewUrl) URL.revokeObjectURL(pendingImagePreviewUrl);
        setPendingImageFile(file);
        setPendingImagePreviewUrl(previewUrl);
        if (fieldKey === 'hero_image') {
            setFormData((prev) => ({
                ...prev,
                overview: { ...(prev.overview || {}), hero_image: 'pending' },
            }));
        }
    };

    const handlePendingImageClear = (fieldKey) => {
        if (pendingImagePreviewUrl) URL.revokeObjectURL(pendingImagePreviewUrl);
        setPendingImageFile(null);
        setPendingImagePreviewUrl('');
        if (!fieldKey || fieldKey === 'hero_image') {
            setFormData((prev) => ({
                ...prev,
                overview: { ...(prev.overview || {}), hero_image: '' },
            }));
        }
    };

    const handlePendingPackageImageSelect = (fieldKey, file, previewUrl) => {
        if (pendingPackageImagePreviewUrl) URL.revokeObjectURL(pendingPackageImagePreviewUrl);
        setPendingPackageImageFile(file);
        setPendingPackageImagePreviewUrl(previewUrl);
        if (fieldKey === 'package_hero_image') {
            setFormData((prev) => ({
                ...prev,
                packages: { ...(prev.packages || {}), package_hero_image: 'pending' },
            }));
        }
    };

    const handlePendingPackageImageClear = (fieldKey) => {
        if (pendingPackageImagePreviewUrl) URL.revokeObjectURL(pendingPackageImagePreviewUrl);
        setPendingPackageImageFile(null);
        setPendingPackageImagePreviewUrl('');
        if (!fieldKey || fieldKey === 'package_hero_image') {
            setFormData((prev) => ({
                ...prev,
                packages: { ...(prev.packages || {}), package_hero_image: '' },
            }));
        }
    };

// Uploads whatever image was locally picked, once we actually have a
// trip row to attach it to. No-op if the user never picked a file.
    const uploadPendingImageIfAny = async (resolvedTripId) => {
        if (!pendingImageFile || !resolvedTripId) return;
        try {
            await uploadPendingTripImage({
                axiosInstance,
                file: pendingImageFile,
                parentId: resolvedTripId,
            });
            if (pendingImagePreviewUrl) URL.revokeObjectURL(pendingImagePreviewUrl);
            setPendingImageFile(null);
            setPendingImagePreviewUrl('');
            const heroUrl = await resolveTripHeroImage(resolvedTripId);
            setTripHeroImage(heroUrl || '');
        } catch (error) {
            console.error('Error uploading pending trip image:', error);
            setDraftSnackbar({
                open: true,
                message: 'Trip saved, but the banner image failed to upload. Please try uploading it again.',
                severity: 'error',
            });
        }
    };

    const isEditMode = Boolean(editTripId);

    const syncStepToUrl = (stepIndex) => {
        const stepKey = getStepConfigKey(stepIndex);
        const nextParams = new URLSearchParams(window.location.search);
        if (nextParams.get('step') === stepKey) return;
        nextParams.set('step', stepKey);
        navigate({ search: `?${nextParams.toString()}` }, { replace: true });
    };

    const setPackageParam = (pkgId) => {
        const p = new URLSearchParams(window.location.search);
        if (p.get('package') === String(pkgId)) return;
        p.set('package', pkgId);
        navigate({ search: `?${p.toString()}` }, { replace: true });
    };

    const scrollToFirstError = (errorList) => {
        if (!errorList?.length) return;

        const tryScroll = (attempt = 0) => {
            const firstErrorKey = errorList[0].key;
            const fieldEl = document.querySelector(`[data-trip-field="${firstErrorKey}"]`);
            if (fieldEl) {
                fieldEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
                const focusable = fieldEl.querySelector('input, textarea, [tabindex]:not([tabindex="-1"])');
                if (focusable && typeof focusable.focus === 'function') {
                    focusable.focus({ preventScroll: true });
                }
                return;
            }
            if (attempt < 8) {
                window.setTimeout(() => tryScroll(attempt + 1), 50);
            }
        };

        window.requestAnimationFrame(() => tryScroll(0));
    };

    const showCountryAlreadyExistsError = (message = COUNTRY_ALREADY_EXISTS_MESSAGE) => {
        const countryErrors = [{ key: 'country', message }];
        const overviewStepIndex = WIZARD_STEP_CONFIG_KEYS.indexOf('overview');

        setApiError(message);
        setErrors(countryErrors);
        setValidationSnackbar({
            open: true,
            message,
        });

        if (overviewStepIndex >= 0 && activeStep !== overviewStepIndex) {
            setActiveStep(overviewStepIndex);
            syncStepToUrl(overviewStepIndex);
        }

        scrollToFirstError(countryErrors);
    };

    const getValidationReadyFormData = (source = formData) => (
        applyTripCurrencyToFormData(deepTrimStrings(source), getTripCurrency(source))
    );

    const reviewStepIndex = wizard_steps.length - 1;

    const redirectToFirstInvalidStep = (validationResult) => {
        const stepErrors = validationResult.errorsByStep[validationResult.firstInvalidStepKey] || [];
        setErrors(stepErrors);
        setValidationSnackbar({
            open: true,
            message: `Complete all wizard steps before publishing. ${formatValidationMessage(stepErrors, {
                stepLabel: validationResult.firstInvalidStepLabel,
                fieldsMap: stepsConfig[validationResult.firstInvalidStepKey]?.fields,
            })}`,
        });
        scrollToFirstError(stepErrors);
        setActiveStep(validationResult.firstInvalidStepIndex);
        syncStepToUrl(validationResult.firstInvalidStepIndex);
    };

    const assertAllStepsValidForPublish = (sourceFormData = formData) => {
        const readyFormData = getValidationReadyFormData(sourceFormData);
        const validation = validateAllWizardSteps(readyFormData);
        if (!validation.isValid) {
            redirectToFirstInvalidStep(validation);
            return { ok: false, formData: readyFormData };
        }
        return { ok: true, formData: readyFormData };
    };

    const handleStepClick = (index) => {
        if (index === activeStep) return;

        if (!isEditMode) {
            if (index === reviewStepIndex) {
                const validation = validateAllWizardSteps(getValidationReadyFormData());
                if (!validation.isValid) {
                    redirectToFirstInvalidStep(validation);
                    return;
                }
            }
            if (!completedSteps.has(index) && index > activeStep) return;
        }

        setErrors([]);
        setActiveStep(index);
        syncStepToUrl(index);
    };

    useEffect(() => {
        scroller.scrollTo({ top: 0, behavior: 'smooth' });
        if (isContinuing) {
            setIsContinuing(false);
        }
    }, [activeStep]);

    const [paymentTemplates, setPaymentTemplates] = useState([]);
    const [allPaymentTemplates, setAllPaymentTemplates] = useState([]);
    const [templateModalOpen, setTemplateModalOpen] = useState(false);
    const [editingTemplate, setEditingTemplate] = useState(null);

    const [hssTemplates, setHssTemplates] = useState([]);
    const [hssModalOpen, setHssModalOpen] = useState(false);
    const hssSelectCallbackRef = useRef(null);

    const [tripComponents, setTripComponents] = useState([]);
    /** Saved trip logistics for this destination (all packages on this trip). */
    const [destinationLogistics, setDestinationLogistics] = useState([]);
    const [merchants, setMerchants] = useState([]);
    const [allocationDepartures, setAllocationDepartures] = useState([]);
    const [cityOptions, setCityOptions] = useState([]);
    const [isCitiesLoading, setIsCitiesLoading] = useState(false);
    const [componentModalOpen, setComponentModalOpen] = useState(false);
    const componentSelectCallbackRef = useRef(null);
    const [searchComponentModalOpen, setSearchComponentModalOpen] = useState(false);
    const [isUpdatingHss, setIsUpdatingHss] = useState(false);
    const [isImportingComponent, setIsImportingComponent] = useState(false);
    const importComponentContextRef = useRef(null);

    const [itineraryDaysList, setItineraryDaysList] = useState([]);

    const isOtherOnlyCityGrouping = (items) =>
        Array.isArray(items)
        && items.length > 0
        && items.every((item) => (item?.state || 'Other') === 'Other');

    const tripCountry = useMemo(() => {
        const primary = String(formData?.overview?.country || '').trim();
        if (primary) return primary;
        const legacyCountries = formData?.overview?.countries;
        if (Array.isArray(legacyCountries) && legacyCountries.length) {
            return String(legacyCountries[0] || '').trim();
        }
        return '';
    }, [formData?.overview?.country, formData?.overview?.countries]);

    /**
     * All countries relevant to this package's city picker: the current
     * destination's own country, plus the primary_country of every trip
     * selected via Packages > linked trips (trip_ids). A package can span
     * several destinations, so its city options must span all of them too.
     */
    const tripCountries = useMemo(() => {
        const set = new Set();
        if (tripCountry) set.add(tripCountry);

        const selectedTripIds = formData?.packages?.trip_ids || [];
        selectedTripIds.forEach((id) => {
            const trip = tripsList.find((t) => String(t.id) === String(id));
            const country = String(trip?.primary_country || '').trim();
            if (country) set.add(country);
        });

        return Array.from(set);
    }, [tripCountry, formData?.packages?.trip_ids, tripsList]);

    const mapPackageComponentsForLookup = (components = []) =>
        (components || [])
            .filter((c) => c?.id != null && c.id !== '')
            .map((c) => {
                const name = c.name || c.service_name || `Logistics #${c.id}`;
                const category = c.category ? ` (${c.category})` : '';
                return {
                    id: c.id,
                    label: `${name}${category}`,
                    category: c.category,
                    min_occupancy: c.min_occupancy,
                    max_occupancy: c.max_occupancy,
                    min_passenger: c.min_passenger,
                    max_passenger: c.max_passenger,
                    minimum_participants: c.minimum_participants,
                    maximum_participants: c.maximum_participants,
                    cost_price: c.cost_price,
                };
            });

    const mapDeparturesForLookup = (departures = []) =>
        (departures || [])
            .filter((d) => d?.id != null && d.id !== '')
            .map((d) => {
                const dateRaw = d.departure_date
                    ? String(d.departure_date).slice(0, 10)
                    : null;
                const returnRaw = d.return_date
                    ? String(d.return_date).slice(0, 10)
                    : null;
                const dateLabel = dateRaw || `Departure #${d.id}`;
                const city = d.departure_city ? ` · ${d.departure_city}` : '';
                return {
                    id: d.id,
                    label: `${dateLabel}${city}`,
                    date: dateRaw,
                    return_date: returnRaw,
                    total_allocation: d.total_allocation ?? d.maximum_pax ?? null,
                };
            });

    const fetchTripComponents = async () => {
        try {
            if (!tripId) {
                setDestinationLogistics([]);
                return;
            }
            const packageId = formData.packages?.package_id;
            const response = await axiosInstance.get(`/trips/${tripId}`);
            const packages = response.data.packages || [];
            const targetPackage = packageId
                ? packages.find((p) => String(p.id) === String(packageId))
                : undefined;
            const destinationComponents = packages.flatMap((pkg) => pkg.components || []);
            setDestinationLogistics(mapPackageComponentsForLookup(destinationComponents));
            setTripComponents(mapPackageComponentsForLookup(targetPackage?.components || []));
            setAllocationDepartures(mapDeparturesForLookup(targetPackage?.departure_dates || []));
        } catch (error) {
            console.error('Error fetching components:', error);
        }
    };

    const accountHasDmcService = (account) => {
        const raw = account?.service_type;
        const list = Array.isArray(raw)
            ? raw
            : (raw ? String(raw).split(',') : []);
        return list.some((value) => String(value).trim().toLowerCase() === 'dmc');
    };

    /** MerchantAllocation.merchant_id FKs accounts.id (Partner / Merchant), not merchants table.
     * Enrich with merchant-asset + inventory so Allocations can filter and auto-fill
     * Requested Allocation from inventory for the selected departure date.
     */
    const fetchMerchants = async () => {
        try {

            const selectedMerchantIds = [...new Set(
                (formData?.allocations?.allocations || [])
                    .map((row) => row?.merchant_id)
                    .filter((v) => v !== '' && v != null)
            )];

            const [accountsRes, assetsRes, inventoriesRes] = await Promise.all([
                axiosInstance.get('/accounts/', {
                    params: {
                        account_type: 'Partner',
                        status: 'Active',
                        ...(selectedMerchantIds.length ? { include_ids: selectedMerchantIds.join(',') } : {}),
                    },
                }),
                axiosInstance.get('/merchant-assets/').catch(() => ({ data: [] })),
                axiosInstance
                    .get('/merchant-asset-inventories/', { params: { limit: 2000 } })
                    .catch(() => ({ data: [] })),
            ]);
            const partners = Array.isArray(accountsRes.data) ? accountsRes.data : [];
            const merchantAccounts = partners.filter((account) => {
                const partnerType = String(account?.partner_type || '').trim().toLowerCase();
                return partnerType === 'merchant' || partnerType.includes('merchant');
            });

            const assetsByMerchant = new Map();
            (Array.isArray(assetsRes.data) ? assetsRes.data : []).forEach((asset) => {
                if (!asset?.merchant_id) return;
                const entry = assetsByMerchant.get(asset.merchant_id) || {
                    logisticsIds: new Set(),
                    assetTypes: new Set(),
                    totalCapacity: 0,
                    assetCount: 0,
                    assetIds: new Set(),
                };
                entry.assetCount += 1;
                if (asset.id != null) entry.assetIds.add(Number(asset.id));
                if (asset.package_component_id != null && asset.package_component_id !== '') {
                    entry.logisticsIds.add(Number(asset.package_component_id));
                }
                if (asset.asset_type) {
                    entry.assetTypes.add(asset.asset_type);
                }
                const capacity = Number(asset.capacity_per_slot);
                if (Number.isFinite(capacity) && capacity > 0) {
                    entry.totalCapacity += capacity;
                }
                assetsByMerchant.set(asset.merchant_id, entry);
            });

            const inventoriesByMerchant = new Map();
            (Array.isArray(inventoriesRes.data) ? inventoriesRes.data : []).forEach((inv) => {
                const merchantId = inv?.merchant_id;
                if (!merchantId) return;
                const list = inventoriesByMerchant.get(merchantId) || [];
                list.push({
                    date: inv.date ? String(inv.date).slice(0, 10) : null,
                    package_component_id: inv.package_component_id ?? null,
                    merchant_asset_id: inv.merchant_asset_id ?? null,
                    available_capacity: inv.available_capacity,
                    total_capacity: inv.total_capacity,
                    available_units: inv.available_units,
                    qty_total: inv.qty_total,
                    status: inv.status,
                    blackout: Boolean(inv.blackout),
                });
                inventoriesByMerchant.set(merchantId, list);
            });

            setMerchants(
                merchantAccounts.map((account) => {
                    const meta = assetsByMerchant.get(account.id);
                    const inventories = inventoriesByMerchant.get(account.id) || [];
                    const totalCapacity = meta?.totalCapacity || 0;
                    const assetCount = meta?.assetCount || 0;
                    const serviceTypes = Array.isArray(account.service_type)
                        ? account.service_type
                        : (account.service_type ? String(account.service_type).split(',') : []);
                    return {
                        ...account,
                        serviceTypes: serviceTypes.map((value) => String(value).trim()).filter(Boolean),
                        isDmc: accountHasDmcService(account),
                        logisticsIds: meta ? Array.from(meta.logisticsIds) : [],
                        assetTypes: meta ? Array.from(meta.assetTypes) : [],
                        totalCapacity,
                        inventories,
                        hasInventory: inventories.length > 0 || (assetCount > 0 && totalCapacity > 0),
                    };
                })
            );
        } catch (error) {
            console.error('Error fetching merchant accounts:', error);
            setMerchants([]);
        }
    };

    const fetchAllocationLookups = async () => {
        await Promise.all([fetchTripComponents(), fetchMerchants()]);
    };

    const fetchItineraryDaysList = async () => {
        try {
            if (!tripId) return;
            const packageId = formData.packages?.package_id;
            const response = await axiosInstance.get(`/trips/${tripId}`);
            const targetPackage = packageId
                ? (response.data.packages || []).find((p) => p.id === packageId)
                : undefined;
            const itinerary = targetPackage?.itineraries?.[0];
            const days = (itinerary?.itinerary_days || []).map((d) => ({
                id: d.id,
                label: d.day_title ? `Day ${d.day_number}: ${d.day_title}` : `Day ${d.day_number}`,
            }));
            setItineraryDaysList(days);
        } catch (error) {
            console.error('Error fetching itineraries:', error);
        }
    };

    const fetchPaymentTemplates = async () => {
        setIsStepDataLoading(true);
        try {
            const response = await axiosInstance.get('/payment-templates?status=active');
            setPaymentTemplates(response.data);
        } catch (error) {
            console.error('Error fetching payment templates:', error);
            setDraftSnackbar({
                open: true,
                message: getApiErrorMessage(error, { action: 'load payment templates', stepKey: 'pricing' }),
                severity: 'error',
            });
        } finally {
            setIsStepDataLoading(false);
        }
    };

    const fetchHSSTemplates = async () => {
        setIsStepDataLoading(true);
        try {
            const response = await axiosInstance.get('/hss?status=active');
            setHssTemplates(response.data);
        } catch (error) {
            console.error('Error fetching hss templates:', error);
            setDraftSnackbar({
                open: true,
                message: getApiErrorMessage(error, { action: 'load HSS templates', stepKey: 'departures' }),
                severity: 'error',
            });
        } finally {
            setIsStepDataLoading(false);
        }
    };

    // Per-country cache: a country is only ever fetched once per wizard session
    const citiesByCountryRef = useRef(new Map());

    const cityStepActive = CITY_STEP_KEYS.includes(getStepConfigKey(activeStep));

// Stable primitive so a new array with the same countries doesn't retrigger the effect
    const tripCountriesKey = useMemo(
        () => [...tripCountries].sort().join('|'),
        [tripCountries]
    );

    // Review needs names for every attached template, including inactive ones.
    const fetchAllPaymentTemplates = async () => {
        try {
            const response = await axiosInstance.get('/payment-templates');
            setAllPaymentTemplates(Array.isArray(response.data) ? response.data : []);
        } catch (error) {
            console.error('Error fetching payment templates for review:', error);
        }
    };

    useEffect(() => {
        // 1) Only on steps that actually render a city picker
        if (!cityStepActive || !axiosInstance) return undefined;

        if (!tripCountries.length) {
            setCityOptions([]);
            setIsCitiesLoading(false);
            return undefined;
        }

        let cancelled = false;

        const buildOptions = () =>
            tripCountries
                .flatMap((country) => citiesByCountryRef.current.get(country) || [])
                .filter((item) => item?.city && item?.country)
                .reduce((acc, item) => {
                    const key = `${item.country}::${item.state || 'Other'}::${item.city}`;
                    if (!acc.seen.has(key)) {
                        acc.seen.add(key);
                        acc.list.push(item);
                    }
                    return acc;
                }, { seen: new Set(), list: [] })
                .list
                .sort((a, b) =>
                    String(a.country).localeCompare(String(b.country))
                    || String(a.state || '').localeCompare(String(b.state || ''))
                    || String(a.city).localeCompare(String(b.city))
                );

        const loadCities = async () => {
            // 2) Only fetch countries we haven't loaded yet
            const missing = tripCountries.filter((c) => !citiesByCountryRef.current.has(c));

            if (!missing.length) {
                setCityOptions(buildOptions());   // country removed/reordered -> no network call
                setIsCitiesLoading(false);
                return;
            }

            setIsCitiesLoading(true);
            try {
                for (const country of missing) {
                    let items = (await fetchGroupedCitiesForCountries(
                        axiosInstance, [country], { concurrency: 1 }
                    )).flat();
                    if (cancelled) return;

                    if (!items.length || isOtherOnlyCityGrouping(items)) {
                        invalidateGroupedCitiesCache(country);
                        items = (await fetchGroupedCitiesForCountries(
                            axiosInstance, [country], { concurrency: 1, forceRefresh: true }
                        )).flat();
                        if (cancelled) return;
                    }
                    citiesByCountryRef.current.set(country, items);
                }
                setCityOptions(buildOptions());
            } catch (error) {
                console.error('Error loading cities:', error);
                if (!cancelled) setCityOptions([]);
            } finally {
                if (!cancelled) setIsCitiesLoading(false);
            }
        };

        loadCities();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [cityStepActive, tripCountriesKey, axiosInstance]);

    useEffect(() => {

        if (wizard_steps[activeStep] === 'Packages') {
            setFormData((prev) => {
                const pkg = prev.packages || {};
                const hasNoTripsYet = !pkg.trip_ids || pkg.trip_ids.length === 0;
                // Only seed the default before the package has ever been saved —
                // once package_id exists, the user's own selection is authoritative.
                if (!pkg.package_id && hasNoTripsYet && tripId) {
                    return { ...prev, packages: { ...pkg, trip_ids: [tripId] } };
                }
                return prev;
            });
        }

        if (wizard_steps[activeStep] === 'Pricing') {
            fetchPaymentTemplates();
        }

        if (wizard_steps[activeStep] === 'Review') {
            fetchAllPaymentTemplates();
        }

        if (wizard_steps[activeStep] === 'Departures') {
            fetchHSSTemplates();
        }

        if (wizard_steps[activeStep] === 'Allocations') {
            fetchAllocationLookups();
        }

        if (wizard_steps[activeStep] === 'Itineraries') {
            fetchTripComponents();
            const duration = formData.packages?.duration || formData.pricing?.duration || 0;
            const existingDays = formData.itineraries?.days || [];

            const days = Array.from({ length: duration }, (_, i) => {
                return existingDays[i] || {
                    day_number: i + 1,
                    day_title: '',
                    // description: '',
                    line_items: [],
                };
            });

            setFormData((prev) => ({
                ...prev,
                itineraries: {
                    ...prev.itineraries,
                    days,
                },
            }));
        }

        if (wizard_steps[activeStep] === 'Add-On') {
            fetchItineraryDaysList();
            fetchTripComponents();
        }

        const stepKey = getStepConfigKey(activeStep);
        const stepConfig = stepsConfig[stepKey];
        if (stepConfig?.fields) {
            setFormData((prev) => {
                let next = prev;
                if (stepKey === 'departures') {
                    next = applyReturnDatesToDepartures(prev, prev.packages?.duration);
                }
                let stepData = applyDefaults(stepConfig.fields, next[stepKey]);
                if (stepKey === 'departures') {
                    stepData = { ...stepData, departures: sortDeparturesByDate(stepData.departures) };
                }
                return { ...next, [stepKey]: stepData };
            });
        }
    }, [activeStep]);

    const handleNewHssTemplate = (onCreated) => {
        hssSelectCallbackRef.current = typeof onCreated === 'function' ? onCreated : null;
        setHssModalOpen(true);
    };

    const closeHssModal = () => {
        hssSelectCallbackRef.current = null;
        setHssModalOpen(false);
    };

    const handleSaveHssTemplate = async (data) => {
        try {
            setIsUpdatingHss(true);
            const response = await axiosInstance.post('/hss', data);
            const created = response.data;

            if (created?.id) {
                setHssTemplates((prev) => [
                    ...prev.filter((template) => template.id !== created.id),
                    created,
                ]);
                hssSelectCallbackRef.current?.(created);
            }

            setDraftSnackbar({
                open: true,
                message: 'HSS template created successfully.',
                severity: 'success',
            });
            closeHssModal();
        } catch (error) {
            console.error('Error saving hss template:', error);
            setDraftSnackbar({
                open: true,
                message: getApiErrorMessage(error, { action: 'create HSS template', stepKey: 'departures' }),
                severity: 'error',
            });
        } finally {
            setIsUpdatingHss(false);
        }
    };

    const handleChange = (key, value) => {
        const currentStepKey = getStepConfigKey(activeStep);
        const currentStepConfig = stepsConfig[currentStepKey];
        const field = currentStepConfig?.fields?.[key];

        const subFieldsToClear = {};
        if (field?.type !== 'repeatable-form') {
            field?.subFields?.forEach((sub) => {
                Object.keys(sub.fields).forEach((subKey) => {
                    subFieldsToClear[subKey] = '';
                });
            });
        }

        const cloneListValue = (v) => (field?.type === 'repeatable-form' && Array.isArray(v)
            ? v.map((item) => ({ ...(item || {}) }))
            : v);
        const isUpdater = typeof value === 'function';
        const nextValue = isUpdater ? value : cloneListValue(value);

        setFormData((prev) => {
            // Updater form (RepeatableFormField): resolve against the latest state.
            let resolvedValue = isUpdater
                ? cloneListValue(value(prev[currentStepKey]?.[key]))
                : nextValue;

            if (currentStepKey === 'departures' && key === 'departures' && Array.isArray(resolvedValue)) {
                resolvedValue = syncDeparturesReturnDates(
                    prev.departures?.departures,
                    resolvedValue,
                    prev.packages?.duration
                );
            }

            const merged = {
                ...(prev[currentStepKey] || {}),
                ...subFieldsToClear,
                [key]: resolvedValue,
            };
            if (currentStepKey === 'overview' && key === 'continent') {
                merged.country = '';
                setErrors((existing) => existing.filter((err) => err.key !== 'country'));
            }

            if (currentStepKey === 'overview' && key === 'country') {
                if (isCountryTaken(nextValue, takenCountries)) {
                    setErrors((existing) => [
                        ...existing.filter((err) => err.key !== 'country'),
                        { key: 'country', message: COUNTRY_ALREADY_EXISTS_MESSAGE },
                    ]);
                    scrollToFirstError([{ key: 'country', message: COUNTRY_ALREADY_EXISTS_MESSAGE }]);
                } else {
                    setErrors((existing) => existing.filter((err) => err.key !== 'country'));
                }
            }

            let nextFormData = {
                ...prev,
                [currentStepKey]: applyDefaults(currentStepConfig?.fields || {}, merged),
            };

            if (currentStepKey === 'pricing' && key === 'currency' && nextValue) {
                nextFormData = applyTripCurrencyToFormData(nextFormData, nextValue);
            }

            if (currentStepKey === 'packages' && key === 'duration' && nextValue) {
                nextFormData = applyReturnDatesToDepartures(nextFormData, nextValue);
            }

            if (currentStepKey === 'departures' && key === 'departures') {
                const durationErrors = findDepartureDurationErrorsForForm(
                    nextFormData.departures,
                    prev.packages?.duration
                );
                setErrors((existing) => [
                    ...existing.filter((err) => !/^departures\.\d+\.return_date$/.test(err.key)),
                    ...durationErrors,
                ]);
            }

            return nextFormData;
        });

        if (!(currentStepKey === 'departures' && key === 'departures')) {
            setErrors((prev) => prev.filter((e) => e.key !== key && !e.key.startsWith(`${key}.`)));
        }
    };

    const handleCancelTrip = () => {
        setCancelDialogOpen(true);
    };

    const handleCancelDialogClose = () => {
        setCancelDialogOpen(false);
    };

    const handleConfirmCancelTrip = () => {
        setCancelDialogOpen(false);
        navigate('/trips');
    };

    const fetchPackageHeroAttachments = async (pkgId) => {
        const res = await axiosInstance.get('/attachments/', {
            params: { parent_table: 'trip_packages', parent_id: pkgId },
        });
        return (res.data || []).filter((a) =>
            String(a.storage_path || '').includes('trip-package-banners')
        );
    };

    const uploadPendingPackageImageIfAny = async (resolvedPackageId) => {
        if (!pendingPackageImageFile || !resolvedPackageId) return;
        try {
            const existing = await fetchPackageHeroAttachments(resolvedPackageId);

            await uploadPendingTripImage({
                axiosInstance,
                file: pendingPackageImageFile,
                parentId: resolvedPackageId,
                parentTable: 'trip_packages',
                featurePath: 'trip-package-banners',
            });

            // Upload succeeded: remove the old hero(es) so there's only one
            await Promise.all(
                existing.map((a) =>
                    axiosInstance.delete(`/attachments/${a.id}`).catch((e) =>
                        console.error('Failed to delete old package hero', e)
                    )
                )
            );

            if (pendingPackageImagePreviewUrl) URL.revokeObjectURL(pendingPackageImagePreviewUrl);
            setPendingPackageImageFile(null);
            setPendingPackageImagePreviewUrl('');
            const heroUrl = await resolvePackageHeroImage(resolvedPackageId);
            setPackageHeroImage(heroUrl || '');
        } catch (error) {
            console.error('Error uploading pending package image:', error);
            setDraftSnackbar({
                open: true,
                message: 'Package saved, but the image failed to upload. Please try uploading it again.',
                severity: 'error',
            });
        }
    };

    const uploadPendingHighlightImages = async (resolvedTripId, highlights = []) => {
        const updated = [];
        for (const item of highlights) {
            if (!(item?.pendingFile instanceof File)) {
                updated.push(item);
                continue;
            }
            try {
                const attachment = await uploadPendingTripImage({
                    axiosInstance,
                    file: item.pendingFile,
                    parentId: resolvedTripId,
                    parentTable: 'trip_highlights',
                    featurePath: 'trips/highlights',
                });
                let imageUrl = attachment.file_url || '';
                try {
                    imageUrl = await previewPrivateObject({
                        axiosInstance,
                        attachmentId: attachment.id,
                    }) || imageUrl;
                } catch (previewError) {
                    console.error('Error previewing highlight attachment:', previewError);
                }
                updated.push({
                    id: item.id,
                    name: item.name,
                    location: item.location,
                    image: imageUrl,
                    image_attachment_id: attachment.id,
                });
            } catch (error) {
                console.error('Error uploading highlight image:', error);
                updated.push(item);
            }
        }
        return updated;
    };

    const uploadPendingPackageGalleryImages = async (packageId, highlights = []) => {
        const updated = [];
        for (const item of highlights) {
            if (!item?.pendingFile) {
                updated.push({
                    id: item.id,
                    name: item.name,
                    location: item.location,
                    image: item.image,
                    image_attachment_id: item.image_attachment_id || null,
                });
                continue;
            }
            try {
                const attachment = await uploadPendingTripImage({
                    axiosInstance,
                    file: item.pendingFile,
                    parentId: packageId,
                    parentTable: 'packages_highlights',
                    featurePath: 'packages/highlights',
                });
                let imageUrl = '';
                try {
                    imageUrl = await previewPrivateObject({ axiosInstance, attachmentId: attachment.id });
                } catch (previewError) {
                    console.error('Error previewing package gallery attachment:', previewError);
                }
                updated.push({
                    id: item.id,
                    name: item.name,
                    location: item.location,
                    image: imageUrl,
                    image_attachment_id: attachment.id,
                });
            } catch (uploadError) {
                console.error('Error uploading package gallery image:', uploadError);
                updated.push({
                    id: item.id,
                    name: item.name,
                    location: item.location,
                    image: item.image,
                    image_attachment_id: item.image_attachment_id || null,
                });
            }
        }
        return updated;
    };

    const syncPackageGalleryUploads = async (pkgId, fd) => {
        const items = fd.packages?.image_gallery || [];
        if (!pkgId || !items.some((h) => h?.pendingFile)) return fd;
        const uploaded = await uploadPendingPackageGalleryImages(pkgId, items);
        const next = { ...fd, packages: { ...fd.packages, image_gallery: uploaded } };
        await axiosInstance.put(`/trip-wizard-packages/${pkgId}`, sanitizePayload(buildStepPayload('packages', next)));
        return next;
    };

    const syncHighlightUploads = async (id, fd) => {
        const items = fd.overview?.key_highlights || [];
        if (!items.some((h) => h?.pendingFile instanceof File)) return fd;
        const uploaded = await uploadPendingHighlightImages(id, items);
        const next = { ...fd, overview: { ...fd.overview, key_highlights: uploaded } };
        await axiosInstance.put(`/trip-wizard-destination/${id}`, sanitizePayload(buildStepPayload('overview', next)));
        return next;
    };

    const saveStep = async (stepKey, { force = false } = {}) => {
        const fd = formRef.current;
        const packageId = fd.packages?.package_id || null;
        const currentTripId = tripIdRef.current;

        if (!force && !isStepDirty(stepKey, fd)) {
            return { success: true, skipped: true, tripId: currentTripId, packageId };
        }
        if (stepKey !== 'overview' && !currentTripId) {
            return { success: false, message: 'Save the Destination Details step first.' };
        }
        if (!['overview', 'packages'].includes(stepKey) && !packageId) {
            return { success: false, message: 'Save the Package step first.' };
        }
        if (stepKey === 'overview' && isCountryTaken(fd.overview?.country, takenCountries)) {
            showCountryAlreadyExistsError();
            return { success: false, message: COUNTRY_ALREADY_EXISTS_MESSAGE };
        }

        setIsSaving(true);
        setApiError(null);
        try {
            const body = sanitizePayload(buildStepPayload(stepKey, fd));
            const { method, url } = STEP_ENDPOINTS[stepKey]({ tripId: currentTripId, packageId });   // no wrap
            const { data } = await axiosInstance[method](url, body);

            let serverData = data;
            if (data?.warnings?.length) {
                setDraftSnackbar({ open: true, message: data.warnings.join(' '), severity: 'warning' });
                if (stepKey !== 'itineraries') serverData = (await axiosInstance.get(url)).data;   // true state after kept rows
            }

            let next = APPLY_RESPONSE[stepKey] ? APPLY_RESPONSE[stepKey](fd, serverData) : fd;

            if (stepKey === 'overview') {
                const id = currentTripId || data.id;
                setTripId(id);
                await uploadPendingImageIfAny(id);
                next = await syncHighlightUploads(id, next);
                if (!currentTripId) {
                    navigate({ pathname: `/trips/${id}/edit`, search: window.location.search }, { replace: true });
                }
            }
            if (stepKey === 'packages') {
                const pkgId = next.packages.package_id;
                await uploadPendingPackageImageIfAny(pkgId);
                next = await syncPackageGalleryUploads(pkgId, next);
                setPackageParam(pkgId);      // a reload now GETs this package instead of POSTing a second one
            }

            formRef.current = next;
            setFormData(next);
            // Snapshot what we SENT (plus ids), not current state. If the user edited during
            // the request, state != snapshot, so the step correctly stays dirty.
            savedSnapshotRef.current[stepKey] = fingerprint(stepKey, next);
            return { success: true, tripId: tripIdRef.current, packageId: next.packages?.package_id };
        } catch (error) {
            console.error(`Save failed (${stepKey}):`, error);
            const message = getApiErrorMessage(error, { action: 'save', stepKey });
            const detail = error?.response?.data?.detail;
            if (error?.response?.status === 409 && typeof detail === 'string' && /country/i.test(detail)) {
                showCountryAlreadyExistsError(detail.includes('already exists') ? COUNTRY_ALREADY_EXISTS_MESSAGE : detail);
            } else {
                setApiError(message);
            }
            return { success: false, message };
        } finally {
            setIsSaving(false);
        }
    };

    const saveAllDirtySteps = async () => {
        for (const key of STEP_KEYS) {
            const result = await saveStep(key);        // clean steps return immediately
            if (!result.success) return result;
        }
        return { success: true };
    };

    useEffect(() => {
        if (editTripId) {
            if (Number(editTripId) !== tripIdRef.current) {
                fetchTrip(editTripId, editPackageId || searchParams.get('package'));
            } else {
                setIsTripLoading(false);
            }
        } else {
            setIsTripLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [editTripId, editPackageId]);

    const handleContinue = async () => {
        if (isSaving || isContinuing) return;

        if (wizard_steps[activeStep] === 'Review') {
            const publishCheck = assertAllStepsValidForPublish();
            if (!publishCheck.ok) return;
            setPublishDialogOpen(true);
            return;
        }

        setIsContinuing(true);
        setApiError(null);

        const currentStepKey = getStepConfigKey(activeStep);
        const currentStepLabel = wizard_steps[activeStep];
        const currentStepConfig = stepsConfig[currentStepKey];

        if (!currentStepConfig?.fields) {
            setCompletedSteps((prev) => new Set([...prev, activeStep]));
            const nextStep = activeStep + 1;
            setActiveStep(nextStep);
            syncStepToUrl(nextStep);
            setIsContinuing(false);
            return;
        }

        const currencySyncedFormData = applyTripCurrencyToFormData(
            formData,
            getTripCurrency(formData)
        );

        const { errors: allErrors, trimmedFormData } = validateWizardStep({
            stepKey: currentStepKey,
            stepConfig: currentStepConfig,
            formData: currencySyncedFormData,
        });

        if (currentStepKey === 'overview') {
            const selectedCountry = trimmedFormData?.overview?.country;
            if (isCountryTaken(selectedCountry, takenCountries)) {
                allErrors.push({
                    key: 'country',
                    message: COUNTRY_ALREADY_EXISTS_MESSAGE,
                });
            }
        }

        if (allErrors.length > 0) {
            const countryError = allErrors.find((err) => err.key === 'country');
            if (countryError && isCountryTaken(trimmedFormData?.overview?.country, takenCountries)) {
                showCountryAlreadyExistsError(countryError.message);
                setIsContinuing(false);
                return;
            }

            setErrors(allErrors);
            setValidationSnackbar({
                open: true,
                message: currentStepKey === 'allocations'
                    ? 'please input required fields'
                    : formatValidationMessage(allErrors, {
                        stepLabel: currentStepLabel,
                        fieldsMap: currentStepConfig.fields,
                    }),
            });
            scrollToFirstError(allErrors);
            setIsContinuing(false);
            return;
        }

        setFormData(trimmedFormData);
        formRef.current = trimmedFormData;

        const nextStep = activeStep + 1;
        if (!isEditMode && nextStep === reviewStepIndex) {
            const fullValidation = validateAllWizardSteps(trimmedFormData);
            if (!fullValidation.isValid) {
                redirectToFirstInvalidStep(fullValidation);
                setIsContinuing(false);
                return;
            }
        }

        const saveResult = await saveStep(currentStepKey);   // skips the API call if nothing changed
        if (!saveResult?.success) {
            setIsContinuing(false);
            return;
        }
        setCompletedSteps((prev) => new Set([...prev, activeStep]));
        setActiveStep(nextStep);
        syncStepToUrl(nextStep);
        setIsContinuing(false);
    };

    const handleSaveDraft = async () => {
        const key = getStepConfigKey(activeStep);
        if (key === 'departures' || key === 'allocations') {
            const stepConfig = stepsConfig[key];
            const fd = formRef.current || formData;
            const crossErrors = findCrossFieldErrors(stepConfig?.fields, fd[key]);
            const durationErrors = key === 'departures'
                ? findDepartureDurationErrorsForForm(fd[key], fd.packages?.duration)
                : [];
            const blocking = [...crossErrors, ...durationErrors];
            if (blocking.length > 0) {
                setErrors(blocking);
                setValidationSnackbar({
                    open: true,
                    message: formatValidationMessage(blocking, {
                        stepLabel: wizard_steps[activeStep],
                        fieldsMap: stepConfig.fields,
                    }),
                });
                scrollToFirstError(blocking);
                return;
            }
        }
        const result = STEP_KEYS.includes(key) ? await saveStep(key) : { success: true };
        setDraftSnackbar(result.success
            ? { open: true, message: result.skipped ? 'No changes to save.' : 'Progress saved.', severity: 'success' }
            : { open: true, message: result.message || 'Could not save. Fix any issues on this step and try again.', severity: 'error' });
    };

    const handlePublishDialogClose = () => {
        setPublishDialogOpen(false);
    };

    const handlePublishDecline = async () => {
        setPublishDialogOpen(false);
        setIsContinuing(true);
        const r = await saveAllDirtySteps();
        setIsContinuing(false);
        setDraftSnackbar(r.success
            ? { open: true, message: 'Progress saved as draft.', severity: 'success' }
            : { open: true, message: r.message || 'Could not save.', severity: 'error' });
    };

    const handlePublishConfirm = async () => {
        setPublishDialogOpen(false);
        setIsContinuing(true);
        setApiError(null);
        try {
            const publishCheck = assertAllStepsValidForPublish();
            if (!publishCheck.ok) { setIsContinuing(false); return; }
            const saved = await saveAllDirtySteps();
            if (!saved.success) { setIsContinuing(false); return; }
            await axiosInstance.post(`/trips/${tripIdRef.current}/publish`);   // backend sets trip/package/itinerary active

            setDraftSnackbar({
                open: true,
                message: 'Trip published and set to Active.',
                severity: 'success',
            });
            navigate('/trips');
        } finally {
            setIsContinuing(false);
        }
    };

    const handleBack = () => {
        setErrors([]);
        const prevStep = activeStep - 1;
        setActiveStep(prevStep);
        syncStepToUrl(prevStep);
    };

    const togglePaymentTemplateSelection = (key, templateId) => {
        setFormData((prev) => {
            const pricing = prev.pricing || {};
            const current = pricing[key] || [];
            const updated = current.includes(templateId)
                ? current.filter((id) => id !== templateId)
                : [...current, templateId];
            return {
                ...prev,
                pricing: {
                    ...pricing,
                    [key]: updated,
                },
            };
        });
    };

    const handleNewTemplate = () => {
        setEditingTemplate(null);
        setTemplateModalOpen(true);
    };

    const handleNewComponent = (onCreated) => {
        componentSelectCallbackRef.current = typeof onCreated === 'function' ? onCreated : null;
        setComponentModalOpen(true);
    };

    const closeSearchComponentModal = () => {
        componentSelectCallbackRef.current = null;
        importComponentContextRef.current = null;
        setSearchComponentModalOpen(false);
    };

    const handleSearchExistingComponent = (onSelected, attachContext = null) => {
        componentSelectCallbackRef.current = typeof onSelected === 'function' ? onSelected : null;
        importComponentContextRef.current = attachContext || null;
        setSearchComponentModalOpen(true);
    };

    const handleImportExistingComponent = async (sourceComponentId, sourceRow, attachContext) => {
        setIsImportingComponent(true);
        try {
            const resolvedTripId = await ensureTripPackageReady();
            if (!resolvedTripId) {
                throw new Error('Complete Overview and Packages steps before importing services.');
            }

            const response = await axiosInstance.post(
                `/trips/${resolvedTripId}/import-component/${sourceComponentId}`
            );
            const imported = response.data;
            const attachCtx = attachContext || importComponentContextRef.current;
            const wasClean = !isStepDirty('services');
            setFormData((prev) => {
                const tripCurrency = getTripCurrency(prev);
                const services = [...(prev.services?.services || [])];
                const existsIndex = services.findIndex((s) => s.id === imported.id);
                const normalizedImport = tripCurrency
                    ? { ...imported, currency: tripCurrency }
                    : imported;
                if (existsIndex === -1) {
                    services.push(normalizedImport);
                } else {
                    services[existsIndex] = { ...services[existsIndex], ...normalizedImport };
                }

                let next = {
                    ...prev,
                    services: { ...(prev.services || {}), services },
                };

                if (attachCtx && attachCtx.dayIndex != null && attachCtx.itemIndex != null) {
                    const days = [...(next.itineraries?.days || [])];
                    const day = days[attachCtx.dayIndex];
                    if (day) {
                        const lineItems = [...(day.line_items || [])];
                        const current = lineItems[attachCtx.itemIndex] || {};
                        lineItems[attachCtx.itemIndex] = {
                            ...current,
                            trip_component_map: imported.id,
                            item_title: current.item_title || imported.service_name || sourceRow?.name || '',
                        };
                        days[attachCtx.dayIndex] = { ...day, line_items: lineItems };
                        next = { ...next, itineraries: { ...next.itineraries, days } };
                    }
                }

                return next;
            });

            await fetchTripComponents();
            if (wasClean) savedSnapshotRef.current.services = fingerprint('services', formRef.current);

            if (componentSelectCallbackRef.current) {
                componentSelectCallbackRef.current(imported.id);
            }

            setDraftSnackbar({
                open: true,
                message: attachCtx
                    ? 'Service imported and attached to the day line item.'
                    : 'Service imported from existing trip.',
                severity: 'success',
            });
            closeSearchComponentModal();
        } catch (error) {
            console.error('Import component failed:', error);
            setDraftSnackbar({
                open: true,
                message: getApiErrorMessage(error, {
                    action: 'import service',
                    stepKey: getStepConfigKey(activeStep),
                }),
                severity: 'error',
            });
        } finally {
            setIsImportingComponent(false);
        }
    };

    const handleSaveNewComponent = async (componentData) => {
        await ensureTripPackageReady();

        const currency = getTripCurrency(formRef.current);
        const newService = currency ? { ...componentData, currency } : componentData;

        const before = formRef.current;
        const withNew = {
            ...before,
            services: {
                ...(before.services || {}),
                services: [...(before.services?.services || []), newService],
            },
        };
        formRef.current = withNew;
        setFormData(withNew);

        const result = await saveStep('services');
        if (!result.success) {
            // roll back so the failed row doesn't linger in the form
            formRef.current = before;
            setFormData(before);
            throw new Error(result.message || 'Unable to save the new service. Fix any trip errors and try again.');
        }

        const saved = formRef.current.services?.services || [];
        const created = saved[saved.length - 1];

        setTripComponents(mapPackageComponentsForLookup(
            saved.map((s) => ({ ...s, name: s.service_name }))
        ));

        if (created?.id != null && componentSelectCallbackRef.current) {
            componentSelectCallbackRef.current(created.id);
        }
        componentSelectCallbackRef.current = null;
        setComponentModalOpen(false);
    };

    const handleEditTemplate = (template) => {
        setEditingTemplate(template);
        setTemplateModalOpen(true);
    };

    const handleSaveTemplate = async (data) => {
        try {
            if (editingTemplate) {
                await axiosInstance.put(`/payment-templates/${editingTemplate.id}`, data);
                setDraftSnackbar({
                    open: true,
                    message: 'Payment template updated successfully.',
                    severity: 'success',
                });
            } else {
                await axiosInstance.post('/payment-templates', data);
                setDraftSnackbar({
                    open: true,
                    message: 'Payment template created successfully.',
                    severity: 'success',
                });
            }
            fetchPaymentTemplates();
            setTemplateModalOpen(false);
        } catch (error) {
            console.error('Error saving template:', error);
            setDraftSnackbar({
                open: true,
                message: getApiErrorMessage(error, { action: 'save payment template', stepKey: 'pricing' }),
                severity: 'error',
            });
        }
    };

    const handleDayChange = (dayIndex, key, value) => {
        setFormData((prev) => {
            const days = [...(prev.itineraries.days || [])];
            days[dayIndex] = { ...days[dayIndex], [key]: value };
            return { ...prev, itineraries: { ...prev.itineraries, days } };
        });
    };

    const handleAddLineItem = (dayIndex) => {
        setFormData((prev) => {
            const days = [...(prev.itineraries.days || [])];
            const lineItems = [...(days[dayIndex].line_items || [])];
            const maxSequence = lineItems.reduce((max, item) => {
                const seq = parseInt(item.sequence, 10);
                return Number.isFinite(seq) ? Math.max(max, seq) : max;
            }, 0);
            lineItems.push({
                item_title: '',
                trip_component_map: null,
                location: '',
                sequence: maxSequence + 1,
                day_line_item_description: '',
            });
            days[dayIndex] = { ...days[dayIndex], line_items: lineItems };
            return { ...prev, itineraries: { ...prev.itineraries, days } };
        });
    };

    const handleLineItemChange = (dayIndex, itemIndex, key, value) => {
        setFormData((prev) => {
            const days = [...(prev.itineraries.days || [])];
            const lineItems = [...(days[dayIndex].line_items || [])];
            lineItems[itemIndex] = { ...lineItems[itemIndex], [key]: value };
            days[dayIndex] = { ...days[dayIndex], line_items: lineItems };
            return { ...prev, itineraries: { ...prev.itineraries, days } };
        });
    };

    const handleRemoveLineItem = (dayIndex, itemIndex) => {
        setFormData((prev) => {
            const days = [...(prev.itineraries.days || [])];
            const lineItems = days[dayIndex].line_items.filter((_, i) => i !== itemIndex);
            days[dayIndex] = { ...days[dayIndex], line_items: lineItems };
            return { ...prev, itineraries: { ...prev.itineraries, days } };
        });
    };

    const applyWizardStepFromQuery = (stepKey, loadedFormData) => {
        if (!stepKey) return;
        const normalized = stepKey.toLowerCase();
        let stepIndex = WIZARD_STEP_CONFIG_KEYS.indexOf(normalized);
        if (stepIndex < 0) {
            stepIndex = wizard_steps.findIndex((step) => step.toLowerCase() === normalized);
        }
        if (stepIndex < 0) return;

        const readyFormData = getValidationReadyFormData(loadedFormData || formData);
        const completed = computeInitialCompletedSteps(readyFormData);

        if (!isEditMode) {
            if (stepIndex === reviewStepIndex) {
                const validation = validateAllWizardSteps(readyFormData);
                if (!validation.isValid) {
                    stepIndex = validation.firstInvalidStepIndex;
                }
            } else {
                const maxCompleted = completed.size > 0 ? Math.max(...completed) : -1;
                if (stepIndex > maxCompleted + 1) {
                    stepIndex = Math.min(maxCompleted + 1, reviewStepIndex - 1);
                }
            }
        }

        setCompletedSteps(completed);
        setActiveStep(stepIndex);
    };

    useEffect(() => {
        if (isTripLoading) return;
        const stepKey = searchParams.get('step');
        if (stepKey) {
            applyWizardStepFromQuery(stepKey, formData);
            return;
        }
        syncStepToUrl(activeStep);
    }, [isTripLoading, editTripId]);

    const pageTitle = editTripId
        ? (editPackageId ? 'Edit Package' : 'Edit Destination')
        : 'Create Destination';

    const ensureTripId = async () => {
        if (tripIdRef.current) return tripIdRef.current;
        const r = await saveStep('overview');
        return r.success ? r.tripId : null;
    };

    /** Persist overview + package so import-component / add-component has a TripPackage row. */
    const ensureTripPackageReady = async () => {
        // 1) Destination first (skipped automatically if already saved and unchanged)
        const overviewResult = await saveStep('overview');
        if (!overviewResult.success) {
            throw new Error(overviewResult.message || 'Complete the Destination Details step first.');
        }

        // 2) Fill package defaults into the latest form, then save the package
        const fd = formRef.current;
        const pkg = fd.packages || {};
        const withDefaults = {
            ...fd,
            packages: {
                ...pkg,
                package_title: pkg.package_title || 'Default Package',
                duration: pkg.duration ?? fd.pricing?.duration,
                trip_ids: pkg.trip_ids?.length ? pkg.trip_ids : [tripIdRef.current],
            },
        };
        formRef.current = withDefaults;
        setFormData(withDefaults);

        const packageResult = await saveStep('packages');
        if (!packageResult.success) {
            throw new Error(packageResult.message || 'Complete the Package step before adding services.');
        }
        return tripIdRef.current;
    };


    const closeComponentModal = () => {
        componentSelectCallbackRef.current = null;
        setComponentModalOpen(false);
    };

    const tripCurrency = getTripCurrency(formData);

    const lookupOptions = useMemo(() => {
        const packageId = formData.packages?.package_id;
        const packageTitle = formData.packages?.package_title;
        const trip_packages = packageId
            ? [{ value: packageId, label: packageTitle || `Package #${packageId}` }]
            : [];

        const mergeByValue = (items) => {
            const byId = new Map();
            items.forEach((item) => {
                if (item?.value == null || item.value === '') return;
                byId.set(String(item.value), item);
            });
            return Array.from(byId.values());
        };

        const formDepartures = mapDeparturesForLookup(formData.departures?.departures || []).map((d) => ({
            value: d.id,
            label: d.label,
            date: d.date,
            return_date: d.return_date,
            total_allocation: d.total_allocation,
        }));
        const apiDepartures = (allocationDepartures || []).map((d) => ({
            value: d.id,
            label: d.label,
            date: d.date,
            return_date: d.return_date,
            total_allocation: d.total_allocation,
        }));
        const departure_dates = mergeByValue([...apiDepartures, ...formDepartures]);

        const currentPackageLogisticsIds = new Set(
            (tripComponents || [])
                .map((c) => (c?.id != null ? String(c.id) : null))
                .filter(Boolean)
        );

        const formLogistics = mapPackageComponentsForLookup(
            (formData.services?.services || []).map((s) => ({
                id: s.id,
                name: s.service_name || s.name,
                category: s.category,
            }))
        ).map((c) => ({
            value: c.id,
            label: c.label,
            category: c.category,
            isCurrentPackage: true,
        }));
        const packageLogistics = (tripComponents || []).map((c) => ({
            value: c.id,
            label: c.label || `Logistics #${c.id}`,
            category: c.category,
            isCurrentPackage: true,
        }));
        const savedDestinationLogistics = (destinationLogistics || []).map((c) => ({
            value: c.id,
            label: c.label || `Logistics #${c.id}`,
            category: c.category,
            isCurrentPackage: currentPackageLogisticsIds.has(String(c.id)),
        }));

        // This destination only: in-progress wizard logistics plus already-saved
        // logistics on this trip's packages — never other destinations.
        const trip_logistics = mergeByValue([
            ...savedDestinationLogistics,
            ...packageLogistics,
            ...formLogistics,
        ]).sort((a, b) => {
            const aCurrent = a.isCurrentPackage ? 0 : 1;
            const bCurrent = b.isCurrentPackage ? 0 : 1;
            if (aCurrent !== bCurrent) return aCurrent - bCurrent;
            return String(a.label || '').localeCompare(String(b.label || ''));
        });

        const merchantOptions = (merchants || []).map((m) => ({
            value: m.id,
            label: m.name || `Merchant #${m.id}`,
            serviceTypes: m.serviceTypes || [],
            isDmc: Boolean(m.isDmc),
            logisticsIds: m.logisticsIds || [],
            assetTypes: m.assetTypes || [],
            totalCapacity: m.totalCapacity || 0,
            inventories: m.inventories || [],
            hasInventory: Boolean(m.hasInventory),
        }));

        return {
            merchants: merchantOptions,
            trip_packages,
            departure_dates,
            trip_logistics,
            // Keep legacy key so older configs still resolve.
            service_components: trip_logistics,
        };
    }, [
        formData.packages?.package_id,
        formData.packages?.package_title,
        formData.departures?.departures,
        formData.services?.services,
        tripComponents,
        destinationLogistics,
        allocationDepartures,
        merchants,
    ]);

    const allocationItemDefaults = useMemo(() => {
        const packageId = formData.packages?.package_id;
        // Default Trip Logistics to the first item from this package wizard.
        const defaultLogisticsId =
            tripComponents?.[0]?.id
            ?? formData.services?.services?.find((s) => s?.id != null && s.id !== '')?.id
            ?? '';
        return {
            status: 'Pending',
            pricing_model: 'Dated',
            allocation_priority: 0,
            priority: 0,
            currency: tripCurrency || 'GBP',
            trip_package_id: packageId || '',
            package_component_id: defaultLogisticsId || '',
            cost: '',
            season_from: '',
            season_to: '',
        };
    }, [
        formData.packages?.package_id,
        formData.services?.services,
        tripComponents,
        tripCurrency,
    ]);

    const stepProps = {
        tripCountry,
        tripCountries,
        takenCountries,
        tripId,
        ensureTripId,
        tripHeroImage,
        pendingImagePreviewUrl,
        onPendingImageSelect: handlePendingImageSelect,
        onPendingImageClear: handlePendingImageClear,
        formData,
        tripCurrency,
        onChange: handleChange,
        errors,
        setErrors,
        isStepDataLoading,
        tripStatus,
        hssTemplates,
        paymentTemplates,
        allPaymentTemplates,
        tripsList,
        isTripsLoading,
        packageHeroImage,
        pendingPackageImagePreviewUrl,
        handlePendingPackageImageSelect,
        handlePendingPackageImageClear,
        onToggleTemplate: togglePaymentTemplateSelection,
        onNewTemplate: handleNewTemplate,
        onEditTemplate: handleEditTemplate,
        onDayChange: handleDayChange,
        onAddLineItem: handleAddLineItem,
        onLineItemChange: handleLineItemChange,
        onRemoveLineItem: handleRemoveLineItem,
        tripComponents,
        itineraryDaysList,
        cityOptions,
        isCitiesLoading,
        onNewComponent: handleNewComponent,
        onSearchExistingComponent: handleSearchExistingComponent,
        onNewHssTemplate: handleNewHssTemplate,
        lookupOptions,
        allocationItemDefaults,
    };

    return {
        pageTitle,
        wizard_steps,
        activeStep,
        completedSteps,
        isEditMode,
        apiError,
        isSaving,
        isContinuing,
        isTripLoading,
        isStepDataLoading,
        validationSnackbar,
        draftSnackbar,
        cancelDialogOpen,
        publishDialogOpen,
        formData,
        errors,
        setErrors,
        tripId,
        tripHeroImage,
        tripStatus,
        setTripStatus: handleTripStatusChange,
        hssTemplates,
        paymentTemplates,
        tripsList,
        isTripsLoading,
        packageHeroImage,
        pendingPackageImagePreviewUrl,
        handlePendingPackageImageSelect,
        handlePendingPackageImageClear,
        tripComponents,
        itineraryDaysList,
        cityOptions,
        isCitiesLoading,
        templateModalOpen,
        editingTemplate,
        hssModalOpen,
        isUpdatingHss,
        componentModalOpen,
        searchComponentModalOpen,
        isImportingComponent,
        importComponentContextRef,
        componentSelectCallbackRef,
        handleStepClick,
        handleCancelTrip,
        handleCancelDialogClose,
        handleConfirmCancelTrip,
        handlePublishDialogClose,
        handlePublishDecline,
        handlePublishConfirm,
        handleChange,
        handleContinue,
        handleSaveDraft,
        handleBack,
        ensureTripId,
        handleDayChange,
        handleAddLineItem,
        handleLineItemChange,
        handleRemoveLineItem,
        togglePaymentTemplateSelection,
        handleNewTemplate,
        handleEditTemplate,
        handleSaveTemplate,
        handleNewHssTemplate,
        closeHssModal,
        handleSaveHssTemplate,
        handleNewComponent,
        handleSearchExistingComponent,
        handleImportExistingComponent,
        handleSaveNewComponent,
        closeComponentModal,
        closeSearchComponentModal,
        setTemplateModalOpen,
        setComponentModalOpen,
        setValidationSnackbar,
        setDraftSnackbar,
        stepProps,
    };
}
